// ---------------------------------------------------------------------------
// Lightweight toast notification utility
//
// Renders temporary notification banners in a fixed container at the top-right
// of the viewport. Multiple toasts stack vertically.
// No external dependency needed — uses plain DOM manipulation.
// ---------------------------------------------------------------------------

type ToastVariant = 'success' | 'error' | 'info';

const CONTAINER_ID = 'toast-container';

const VARIANT_STYLES: Record<ToastVariant, string> = {
  success: 'background:#e8f5e9;color:#2e7d32;border:1px solid #a5d6a7;',
  error: 'background:#ffebee;color:#c62828;border:1px solid #ef9a9a;',
  info: 'background:#e3f2fd;color:#1565c0;border:1px solid #90caf9;',
};

const TOAST_STYLES = [
  'padding:12px 20px',
  'border-radius:8px',
  'font-size:14px',
  'font-weight:500',
  'font-family:system-ui,sans-serif',
  'box-shadow:0 4px 12px rgba(0,0,0,0.1)',
  'transition:opacity 0.3s ease',
  'max-width:400px',
].join(';');

const DURATION_MS = 4000;

function getOrCreateContainer(): HTMLElement {
  let container = document.getElementById(CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = CONTAINER_ID;
    container.style.cssText = [
      'position:fixed',
      'top:20px',
      'right:20px',
      'z-index:9999',
      'display:flex',
      'flex-direction:column',
      'gap:8px',
      'pointer-events:none',
    ].join(';');
    document.body.appendChild(container);
  }
  return container;
}

export function toast(message: string, variant: ToastVariant = 'success'): void {
  const container = getOrCreateContainer();

  const el = document.createElement('div');
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.style.cssText = `${TOAST_STYLES};${VARIANT_STYLES[variant]};pointer-events:auto`;
  el.textContent = message;
  container.appendChild(el);

  setTimeout(() => {
    el.style.opacity = '0';
    setTimeout(() => {
      el.remove();
    }, 300);
  }, DURATION_MS);
}

toast.success = (message: string) => toast(message, 'success');
toast.error = (message: string) => toast(message, 'error');
toast.info = (message: string) => toast(message, 'info');
